import { motion } from 'framer-motion';
import dayjs from 'lib/dayjs';
import Icon from '../AppIcon';
import { mapLeaveType } from '../../utils/common';

// ===================== Avatar Component =====================
const Avatar = ({
  name,
  size = 40,
  gradient = 'from-primary-500 to-primary-600',
  className = '',
}) => {
  const initials =
    name
      ?.split(' ')
      ?.map((n) => n[0])
      ?.join('')
      ?.slice(0, 2)
      ?.toUpperCase() || '';

  return (
    <div
      className={`rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold shrink-0 shadow-sm ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
};

// ===================== Status Badge Component =====================
const StatusBadge = ({ status }) => {
  const styles = {
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ACCEPTED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    REJECTED: 'bg-red-50 text-red-700 border-red-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${
        styles[status] || 'bg-slate-50 text-slate-700 border-slate-200'
      }`}
    >
      {status}
    </span>
  );
};

// ===================== Leave Card Component =====================
const LeaveCard = ({
  leave,
  isAdmin = false,
  onApprove,
  onReject,
  onCancel,
  onEdit,
  onBehalf,
  isLoading = false,
  processingId = null,
  className = '',
  onClick,
}) => {
  const isProcessing = isLoading && processingId === leave?.id;
  const start = dayjs(leave?.start_date_time);
  const end = dayjs(leave?.end_date_time);
  const isSameDay =
    start.isValid() && end.isValid() && start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');

  const dateDisplay = () => {
    if (!start.isValid() || !end.isValid()) return 'N/A';
    if (isSameDay) {
      return start.format('MMM DD, YYYY');
    }
    return `${start.format('MMM DD')} - ${end.format('MMM DD')}`;
  };

  const typeColors = {
    // Enum codes
    FULL_DAY_LEAVE: 'bg-sky-50 text-sky-700 border-sky-100',
    HALF_DAY_LEAVE: 'bg-amber-50 text-amber-700 border-amber-100',
    LONG_TIME_LEAVE: 'bg-violet-50 text-violet-700 border-violet-100',
    SHORT_LEAVE: 'bg-rose-50 text-rose-700 border-rose-100',
    MULTIPLE_DAY_LEAVE: 'bg-indigo-50 text-indigo-700 border-indigo-100',

    // Display strings (fallback)
    'Full Day Leave': 'bg-sky-50 text-sky-700 border-sky-100',
    'Half Day Leave': 'bg-amber-50 text-amber-700 border-amber-100',
    'Long Time Leave': 'bg-violet-50 text-violet-700 border-violet-100',
    'Short Leave': 'bg-rose-50 text-rose-700 border-rose-100',
    'Multiple Day Leave': 'bg-indigo-50 text-indigo-700 border-indigo-100',

    // Reference styles
    'Sick Leave': 'bg-rose-50 text-rose-700 border-rose-100',
    'Annual Leave': 'bg-sky-50 text-sky-700 border-sky-100',
    'Personal Leave': 'bg-violet-50 text-violet-700 border-violet-100',
  };

  const displayName = leave?.user
    ? `${leave.user.first_name} ${leave.user.last_name}`
    : 'Unknown Employee';

  const handleAction = (e, callback) => {
    e.stopPropagation();
    callback?.(leave);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`group bg-white border border-slate-200/70 rounded-xl p-4 hover:border-slate-300 hover:shadow-md transition-all duration-300 ${
        onClick ? 'cursor-pointer' : ''
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <Avatar name={displayName} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-slate-900 truncate">{displayName}</h4>
              {leave?.user?.department && (
                <p className="text-[11px] text-slate-400 mt-0.5">{leave.user.department}</p>
              )}
            </div>
            <StatusBadge status={leave?.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                typeColors[leave?.type] || 'bg-slate-50 text-slate-700 border-slate-200'
              }`}
            >
              {mapLeaveType(leave?.type)}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
              <Icon name="Calendar" size={11} className="text-slate-500" />
              {dateDisplay()}
            </span>
            {leave?.days ? (
              <span className="text-[11px] text-slate-400 font-medium">{leave.days}d</span>
            ) : leave?.duration ? (
              <span className="text-[11px] text-slate-400 font-medium">{leave.duration}</span>
            ) : null}
          </div>
          {leave?.reason && (
            <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-1">{leave.reason}</p>
          )}
          {(leave?.created_at || leave?.updated_at) && (
            <div className="mt-2 flex flex-col gap-0.5 text-[10px] text-slate-400">
              {leave?.created_at && (
                <span className="inline-flex items-center gap-1">
                  <Icon name="CalendarPlus" size={10} />
                  Created {dayjs(leave.created_at).format('DD MMM YYYY, hh:mm A')}
                </span>
              )}
              {leave?.updated_at && (
                <span className="inline-flex items-center gap-1">
                  <Icon name="RefreshCw" size={10} />
                  Updated {dayjs(leave.updated_at).format('DD MMM YYYY, hh:mm A')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      {isAdmin && leave?.status === 'PENDING' && (onApprove || onReject) && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          {onApprove && (
            <button
              onClick={(e) => handleAction(e, onApprove)}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Icon name="Loader" size={12} className="animate-spin" />
              ) : (
                <Icon name="Check" size={12} />
              )}
              Approve
            </button>
          )}
          {onReject && (
            <button
              onClick={(e) => handleAction(e, onReject)}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Icon name="Loader" size={12} className="animate-spin" />
              ) : (
                <Icon name="X" size={12} />
              )}
              Reject
            </button>
          )}
        </div>
      )}

      {/* Admin Add Leave on Behalf */}
      {isAdmin && onBehalf && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={(e) => handleAction(e, onBehalf)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs font-semibold transition-colors"
          >
            <Icon name="UserPlus" size={12} />
            Add Leave on Behalf
          </button>
        </div>
      )}

      {/* Employee Actions (Edit/Cancel) */}
      {!isAdmin && leave?.status === 'PENDING' && (onEdit || onCancel) && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          {onEdit && (
            <button
              onClick={(e) => handleAction(e, onEdit)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-lg text-xs font-semibold transition-colors"
            >
              <Icon name="Edit" size={12} />
              Edit
            </button>
          )}
          {onCancel && (
            <button
              onClick={(e) => handleAction(e, onCancel)}
              disabled={isLoading || isProcessing}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Icon name="Loader" size={12} className="animate-spin" />
              ) : (
                <Icon name="Trash2" size={12} />
              )}
              Cancel
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default LeaveCard;
